import axios from 'axios';

class Api {
  static isRefreshing = false;

  static refreshSubscribers = [];

  constructor() {
    this.baseURL = process.env.REACT_APP_AUTH_BASE_URL;
    this.baseURL_KYC = process.env.REACT_APP_AUTH_BASE_URL;

    const axiosInitData = {
      timeout: process.env.REACT_APP_AXIOS_TIMEOUT
    };

    this.axios = axios.create(axiosInitData);

    /**
     * Every request to API should have API key and a access token
     */
    this.axios.interceptors.request.use(
      config => {
        const newConfig = {
          ...config,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        };
        return newConfig;
      },
      error => Promise.reject(error)
    );

    /**
     * Handling our server responses, when errors, get verbose ones
     */
    this.axios.interceptors.response.use(
      response => response,
      error => {
        const {
          config,
          response: { status, data }
        } = error;
        const originalRequest = config;

        // If we need to refresh..
        if (
          status === 400 &&
          (data.message === 'JWT_expired' ||
            data.message_code === 'JWT_expired')
        ) {
          // Call refresh token, if not already refreshing
          if (!Api.isRefreshing) {
            this.refreshAccessToken();
          }

          // Postpone all requests with invalid token in array
          return new Promise(resolve => {
            // we are passing a function, that accepts token, all wrapped in Promise
            Api.subscribeTokenRefresh(token => {
              // replace the expired token and retry
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }
        // Create verbose error, not just error message
        return Promise.reject(error.response);
      }
    );
  }

  static subscribeTokenRefresh(callback) {
    Api.refreshSubscribers.push(callback);
  }

  static onRefreshed(token) {
    // in this case an "fnc" is a function, which we call with new token, its original request
    Api.refreshSubscribers.map(fnc => fnc(token));
    // clear an array after reply
    Api.refreshSubscribers = [];
  }

  setBaseUri = url => {
    this.baseURL = url;
  };

  /**
   * Custom requests
   */
  refreshAccessToken = () =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        Api.isRefreshing = true;
        const res = await this.axios.post(`${this.baseURL}/refresh-token`, {
          refresh_token: localStorage.getItem('refresh_token')
        });
        const { accessToken, refreshToken } = res.data;
        // save new token data into storage
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        Api.isRefreshing = false;
        // after successfull token exchange, reply all requests in our array
        Api.onRefreshed(accessToken);
        resolve({
          accessToken,
          refreshToken
        });
      } catch (error) {
        if (error.status === 400) {
          window.location.replace(localStorage.getItem('websiteUrl'));
        }
        reject(error);
      }
    });

  // Common user data
  getUserData = () => this.axios.get(`${this.baseURL}/area/user`);

  setUserData = data => this.axios.put(`${this.baseURL}/area/user`, data);

  getNationalities = () =>
    this.axios.get(`${this.baseURL_KYC}/merchant/get-nationalities`);

  getCountries = () =>
    this.axios.get(`${this.baseURL_KYC}/merchant/get-countries`);

  // 2FA
  get2FAOptsDisabledInfo = () =>
    this.axios.get(`${this.baseURL}/area/client/authorization-rule`);

  get2FAOptsCheckedInfo = () =>
    this.axios.get(`${this.baseURL}/area/user/authorization-rule`);

  set2FAOpts = data =>
    this.axios.put(`${this.baseURL}/area/user/authorization-rule`, data);

  get2FAVerifications = () =>
    this.axios.get(`${this.baseURL}/area/2fa/verifications`);

  sendSMSVerification = () =>
    this.axios.post(`${this.baseURL}/area/SMS/send-code`);

  authenticateSMSVerification = data =>
    this.axios.post(`${this.baseURL}/area/SMS/authenticate`, data);

  totpRegister = () => this.axios.post(`${this.baseURL}/area/TOTP/register`);

  totpAuthenticate = data =>
    this.axios.put(`${this.baseURL}/area/TOTP/authenticate`, data);

  // Misc
  verifyEmail = data =>
    this.axios.post(`${this.baseURL}/area/emailChange/verifyEmailChange`, data);

  verifyPhone = data =>
    this.axios.post(`${this.baseURL}/area/phoneChange/verifyEmailChange`, data);

  setUserPassword = data =>
    this.axios.put(`${this.baseURL}/area/user/change-password`, data);

  // KYC
  getKycStatus = () => this.axios.get(`${this.baseURL_KYC}/area/kyc`);

  uploadKycDocument = data =>
    this.axios.post(`${this.baseURL_KYC}/area/kyc/document`, data, {
      headers: {
        ...this.axios.headers,
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

  deleteKycDocument = id =>
    this.axios.delete(`${this.baseURL_KYC}/area/kyc/document/${id}`);

  getKycAllowedTypes = () =>
    this.axios.get(`${this.baseURL_KYC}/area/kyc/types`);

  getKycSubtypes = type =>
    this.axios.get(`${this.baseURL_KYC}/area/kyc/subtypes/${type}`);

  cancelKycVerification = () =>
    this.axios.delete(`${this.baseURL}/area/kyc/document`);

  getAvailable2FARequest = () =>
      this.axios.get(`${process.env.REACT_APP_AUTH_BASE_URL}/merchant/get-available-2fa`);

  getInitData = () =>
    this.axios.get(`${process.env.REACT_APP_AUTH_BASE_URL}/merchant/settings`);
}

export default new Api();
